/**
 * APB-1600: BRAIN Assistant conversation state.
 *
 * Design notes:
 * - One slice owns the current conversation (messages + session_id).
 * - Multiple assistants on different dashboards share this slice;
 *   there's only one active conversation per user at a time.
 * - Messages include both user turns (from the input field) and
 *   assistant turns (from the chat endpoint). We deliberately don't
 *   store raw tool_call intermediate turns — the backend handles
 *   those and only persists user-visible turns (matching the
 *   server-side session shape from APB-1588).
 * - Citations are stored per-assistant-message so the UI can render
 *   clickable chips inline with the text.
 *
 * We use a thunk rather than RTK Query because the assistant needs:
 * - Imperative send (not a query that refetches on focus)
 * - Fine-grained loading state per message
 * - Error handling that converts BYOK / rate-limit errors into
 *   in-chat error cards, not toast notifications
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  Citation,
  ChatResponseBody,
  SessionHistoryResponse,
  sendChatMessage,
  getSessionHistory,
  endSession,
} from '../../api/assistantApi';

// ---- Types -----------------------------------------------------------

export interface ChatMessageUI {
  /** Local UUID for React keys. Distinct from the server session_id. */
  localId: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
  citations?: Citation[];
  timestamp: string;
  /** When true, this message is still streaming in (future Phase 4). */
  pending?: boolean;
}

interface AssistantState {
  sessionId: string | null;
  messages: ChatMessageUI[];
  /** True while awaiting a response from /api/assistant/chat. */
  sending: boolean;
  /** Non-null means the last send failed with a retryable error. */
  lastError: string | null;
  /** Count of turns used — useful for token-budget UI in Phase 5. */
  totalPromptTokens: number;
  totalCompletionTokens: number;
}

const initialState: AssistantState = {
  sessionId: null,
  messages: [],
  sending: false,
  lastError: null,
  totalPromptTokens: 0,
  totalCompletionTokens: 0,
};

// ---- Thunks ----------------------------------------------------------

/**
 * Send a user message. Optimistically adds the user turn to state, then
 * dispatches the server round-trip. On success, appends the assistant's
 * reply. On failure, appends an error-role message the UI renders as a
 * red card with a retry affordance.
 */
export const sendAssistantMessage = createAsyncThunk<
  ChatResponseBody,
  { message: string; role?: 'admin' | 'executive' | 'analyst' },
  { rejectValue: string }
>(
  'assistant/send',
  async ({ message, role }, { getState, rejectWithValue }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = (getState() as any).assistant as AssistantState;
    try {
      const res = await sendChatMessage({
        message,
        session_id: state.sessionId ?? undefined,
        role,
      });
      return res;
    } catch (e: unknown) {
      // Unwrap axios error shape for a useful message. Fall back to
      // generic on unknown shape.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any;
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to reach BRAIN Assistant.';
      return rejectWithValue(detail);
    }
  },
);

export const loadSessionHistory = createAsyncThunk<
  SessionHistoryResponse,
  string,
  { rejectValue: string }
>(
  'assistant/loadHistory',
  async (sessionId, { rejectWithValue }) => {
    try {
      return await getSessionHistory(sessionId);
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any;
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Session not found',
      );
    }
  },
);

export const endAssistantSession = createAsyncThunk<void, void>(
  'assistant/end',
  async (_, { getState }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = (getState() as any).assistant as AssistantState;
    if (state.sessionId) {
      try {
        await endSession(state.sessionId);
      } catch {
        // Delete is idempotent on the server; swallow failures so the
        // client can always clear its own state.
      }
    }
  },
);

// ---- Slice -----------------------------------------------------------

const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    resetConversation(state) {
      state.sessionId = null;
      state.messages = [];
      state.sending = false;
      state.lastError = null;
      state.totalPromptTokens = 0;
      state.totalCompletionTokens = 0;
    },
    clearError(state) {
      state.lastError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- sendAssistantMessage -----
      .addCase(sendAssistantMessage.pending, (state, action) => {
        const { message } = action.meta.arg;
        state.messages.push({
          localId: crypto.randomUUID(),
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
        });
        state.sending = true;
        state.lastError = null;
      })
      .addCase(sendAssistantMessage.fulfilled, (state, action) => {
        const body = action.payload;
        state.sessionId = body.session_id;
        state.messages.push({
          localId: crypto.randomUUID(),
          role: 'assistant',
          content: body.content,
          citations: body.citations,
          timestamp: new Date().toISOString(),
        });
        state.sending = false;
        state.totalPromptTokens += body.prompt_tokens;
        state.totalCompletionTokens += body.completion_tokens;
      })
      .addCase(sendAssistantMessage.rejected, (state, action) => {
        state.sending = false;
        const detail = action.payload ?? 'Assistant request failed.';
        state.lastError = detail;
        // Append a visible error message so the user sees what went wrong
        // without waiting for a separate toast.
        state.messages.push({
          localId: crypto.randomUUID(),
          role: 'error',
          content: detail,
          timestamp: new Date().toISOString(),
        });
      })
      // ---- loadSessionHistory ----
      .addCase(loadSessionHistory.fulfilled, (state, action) => {
        const body = action.payload;
        state.sessionId = body.session_id;
        state.messages = body.messages.map((m) => ({
          localId: crypto.randomUUID(),
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
        }));
      })
      // ---- endAssistantSession ----
      .addCase(endAssistantSession.fulfilled, (state) => {
        state.sessionId = null;
        state.messages = [];
        state.totalPromptTokens = 0;
        state.totalCompletionTokens = 0;
      });
  },
});

export const { resetConversation, clearError } = assistantSlice.actions;
export default assistantSlice.reducer;
